export enum BillingType {
  Invoice = 'invoice',
  Receipt = 'receipt'
}

export enum IvaCondition {
  CONSUMIDOR_FINAL = 'consumidor_final',
  RESPONSABLE_INSCRIPTO = 'responsable_inscripto',
  RESPONSABLE_NO_INSCRIPTO = 'responsable_no_inscripto',
  IVA_NO_RESPONSABLE = 'iva_no_responsable',
  IVA_EXENTO = 'iva_exento',
  RESPONSABLE_MONOTRIBUTO = 'responsable_monotributo',
}

export const getIvaConditionOptions = () => {
  const ivaConditionMap: Record<IvaCondition, string> = {
    [IvaCondition.CONSUMIDOR_FINAL]: 'Consumidor Final',
    [IvaCondition.RESPONSABLE_INSCRIPTO]: 'Responsable Inscripto',
    [IvaCondition.RESPONSABLE_NO_INSCRIPTO]: 'Responsable No Inscripto',
    [IvaCondition.IVA_NO_RESPONSABLE]: 'IVA No Responsable',
    [IvaCondition.IVA_EXENTO]: 'IVA Exento',
    [IvaCondition.RESPONSABLE_MONOTRIBUTO]: 'Responsable Monotributo'
  }
  return Object.entries(ivaConditionMap).map(([value, label]) => ({ label, value }))
}

export enum InvoiceStatus {
  DRAFT = 'draft',            // created, it didn't get send to processing yet
  PENDING = 'pending',        // processing in progress
  AUTHORIZED = 'authorized',  // processing authorized
  REJECTED = 'rejected',      // processing rejected due to invalid data
  CANCELLED = 'cancelled',    // processing cancelled, will not be processed anymore
  DELAYED = 'delayed'         // processing was not available, to be processed later
}

// Invoice Type Enum
export enum VoucherType {
  INVOICE = 'invoice',
  CREDIT_NOTE = 'credit_note',
  DEBIT_NOTE = 'debit_note',
  RECEIPT = 'receipt',
  PROFORMA_INVOICE = 'proforma_invoice'
}

export enum InvoiceType {
  A = 'A',
  B = 'B',
  C = 'C',
  M = 'M'
}

// Delivery Option Enum
export enum DeliveryOption {
  None = 'none',
  OfflineEmail = 'offline_email',
  OfflineWasp = 'offline_wasp',
  Online = 'online'
}

export interface InvoiceItem {
  id: string
  description: string
  quantity: number
  unitPrice: number
  taxRate: number
  subtotal: number
  totalAmount: number
  discountPercentage?: number
  discountAmount?: number
  taxAmount?: number
  productCode?: string | null
  afipProductCode?: string | null
  notes?: string | null
}

export interface UpdateInvoiceItemDescriptionDto {
  itemId: string
  description: string
}

export interface InvoiceUserDataDto {
  billingType: BillingType;
}

// Helper Functions
export const formatInvoiceStatus = (status: InvoiceStatus, voucherType: VoucherType): string => {
  const statusMap: Record<InvoiceStatus, string> = {
    [InvoiceStatus.DRAFT]: 'Borrador',
    [InvoiceStatus.PENDING]: 'Procesando',
    [InvoiceStatus.AUTHORIZED]: voucherType === VoucherType.RECEIPT ? 'Recibo' : 'Autorizado',
    [InvoiceStatus.REJECTED]: 'Rechazado',
    [InvoiceStatus.CANCELLED]: 'Cancelado',
    [InvoiceStatus.DELAYED]: 'Reintento'
  }
  return statusMap[status] || status
}

export const formatInvoiceType = (type: VoucherType): string => {
  const typeMap: Record<VoucherType, string> = {
    [VoucherType.INVOICE]: 'Factura',
    [VoucherType.CREDIT_NOTE]: 'Nota de Crédito',
    [VoucherType.DEBIT_NOTE]: 'Nota de Débito',
    [VoucherType.RECEIPT]: 'Recibo',
    [VoucherType.PROFORMA_INVOICE]: 'Proforma'
  }
  return typeMap[type] || type
}

export const formatDeliveryOption = (option: DeliveryOption): string => {
  const optionMap: Record<DeliveryOption, string> = {
    [DeliveryOption.None]: 'Sin envío',
    [DeliveryOption.OfflineEmail]: 'Email offline',
    [DeliveryOption.OfflineWasp]: 'WhatsApp offline',
    [DeliveryOption.Online]: 'Online'
  }
  return optionMap[option] || option
}

export const getInvoiceStatusColor = (status: InvoiceStatus, voucherType: VoucherType): string => {
  const colorMap: Record<InvoiceStatus, string> = {
    [InvoiceStatus.DRAFT]: 'bg-gray-100 text-gray-800 hover:bg-gray-100 hover:text-gray-800',
    [InvoiceStatus.PENDING]: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800',
    [InvoiceStatus.AUTHORIZED]: voucherType === VoucherType.RECEIPT
      ? 'bg-gray-100 text-gray-800 hover:bg-gray-100 hover:text-gray-800' :
      voucherType === VoucherType.INVOICE ?
        'bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800' :
        voucherType === VoucherType.CREDIT_NOTE ?
          'bg-red-100 text-red-800 hover:bg-red-100 hover:text-red-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-100 hover:text-gray-800',
    [InvoiceStatus.REJECTED]: 'bg-red-100 text-red-800 hover:bg-red-100 hover:text-red-800',
    [InvoiceStatus.CANCELLED]: 'bg-red-100 text-red-800 hover:bg-red-100 hover:text-red-800',
    [InvoiceStatus.DELAYED]: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800'
  }
  return colorMap[status] || 'bg-gray-100 text-gray-800'
}

export interface InvoiceSummary {
  totalCount: number
  totalAmount: number
  totalByStatus: {
    cancelledOrRejected: { count: number; amount: number }
    delayed: { count: number; amount: number }
    authorized: { count: number; amount: number }
    authorizedCreditNote: { count: number; amount: number }
  }
}

export interface InvoiceData {
  id: string;
  fileName: string;
  uploadDate: Date;
  paymentId: string;
  voucherType: VoucherType;
  invoiceNumber: string;
}
